import { useState } from 'react';
import { TableContainer, Table, Thead, Tbody, Tr, Th, Td } from '@chakra-ui/react';
import { useToast, Link, Select, Modal, ModalHeader, ModalBody, ModalFooter, Button, Text } from '@mezzoforte/forge';
import { ArrowRight } from '@mezzoforte/forge-icons';
import { useQueryClient } from '@tanstack/react-query';
import { NextLinkButton } from '@/components/Link/LinkButton';
import { ContactView } from '@/hooks/useEntryStats';
import { useCategories } from '@/hooks/useCategories';
import { useLegacyApi } from '@/hooks/useLegacyApi';

interface AdminToolsProps {
  entryId: string;
  categoryId: number;
  calls: ContactView[];
  callsModalOpen: boolean;
  onCallsModalDismiss: () => void;
}

export function AdminTools({ entryId, categoryId, calls, callsModalOpen, onCallsModalDismiss }: AdminToolsProps) {
  const [selectedCategoryId, setSelectedCategoryId] = useState(categoryId);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const queryClient = useQueryClient();
  const { data } = useCategories();
  const { playToast } = useToast();
  const { apiClient } = useLegacyApi();

  const quickEditCategory = async (entryId: string, categoryId: number) => {
    const response = await apiClient.post<{ success: boolean }>(`/api/net-auctions/${entryId}/quick-edit`, {
      categoryId,
    });
    return response.data;
  };

  const flattened = (data?.categories ?? []).flatMap((c) => [c, ...Object.values(c.children)]);

  const handleCategorySelect = (value: number) => {
    setSelectedCategoryId(value);
    setConfirmModalOpen(true);
  };

  const confirmCategoryChange = async () => {
    const r = await quickEditCategory(entryId, selectedCategoryId);
    if (r.success) {
      void queryClient.invalidateQueries({ queryKey: ['currentUser', 'entries', entryId] });
      playToast(null, 'Kohteen osasto vaihdettu', {});
    } else {
      playToast(null, 'Osaston vaihtaminen ei onnistunut', { variant: 'danger' });
      console.error(r);
    }
    setConfirmModalOpen(false);
  };

  const closeConfirmModal = () => {
    setSelectedCategoryId(categoryId);
    setConfirmModalOpen(false);
  };

  return (
    <>
      <Select
        value={selectedCategoryId}
        onChange={(e) => handleCategorySelect(Number(e.target.value))}
        sx={{ paddingInlineStart: 2, display: 'inline-block' }}
        width={180}
        data-test="quick-edit-category-select"
      >
        {flattened.map((category) => (
          // eslint-disable-next-line jsx-a11y/control-has-associated-label
          <option
            value={category.id}
            key={category.id}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: category.parentId ? ` - ${category.htmlName}` : category.htmlName }}
          />
        ))}
      </Select>
      <NextLinkButton
        href={`/hallinta/nettihuutokaupat/${entryId}`}
        variant="primary"
      >
        Muokkaa
      </NextLinkButton>
      <NextLinkButton href={`/yllapito/kohteen-tiedot/${entryId}`}>Tiedot</NextLinkButton>
      <NextLinkButton
        download
        target="_blank"
        href={`/api/net-auctions/${entryId}/images/download/file`}
      >
        Lataa kaikki kuvat
      </NextLinkButton>
      <Modal
        isOpen={confirmModalOpen}
        onDismiss={closeConfirmModal}
      >
        <ModalHeader>Haluatko varmasti vaihtaa kohteen osaston?</ModalHeader>
        <ModalBody>
          <Text textAlign="center">
            {flattened.find((c) => c.id === categoryId)?.htmlName} <ArrowRight style={{ verticalAlign: 'bottom' }} />{' '}
            {flattened.find((c) => c.id === selectedCategoryId)?.htmlName}
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button onClick={closeConfirmModal}>Peruuta</Button>
          <Button
            onClick={confirmCategoryChange}
            variant="primary"
          >
            Vahvista
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        isOpen={callsModalOpen}
        onDismiss={onCallsModalDismiss}
      >
        <ModalHeader>Soitot</ModalHeader>
        <ModalBody>
          <TableContainer>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Aika</Th>
                  <Th>Asiakas</Th>
                </Tr>
              </Thead>
              <Tbody>
                {calls.map((call) => (
                  <Tr key={call.id}>
                    <Td>{call.created_at}</Td>
                    <Td>
                      <Link href={`/yllapito/kayttaja/${call.user_id}`}>
                        {call.user_first_name} {call.user_last_name}
                      </Link>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onCallsModalDismiss}>Sulje</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
